<template>
  <div class="min-h-screen bg-gray-50">
    <!-- 页面标题 -->
    <div class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">{{ $store.getters.t('recruitment_agent') }}</h1>
            <p class="text-sm text-gray-600">{{ $store.getters.t('recruitment_agent_desc') }}</p>
          </div>
          <div class="flex items-center space-x-4">
            <div class="flex items-center space-x-2">
              <div class="w-3 h-3 bg-green-500 rounded-full"></div>
              <span class="text-sm text-gray-600">{{ $store.getters.t('system_online') }}</span>
            </div>
            <button 
              @click="loadResumes"
              class="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <i class="fas fa-sync-alt mr-2"></i>
              {{ $store.getters.t('refresh') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- 统计卡片 -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
              <i class="fas fa-users text-blue-600 text-xl"></i>
            </div>
            <div>
              <p class="text-sm text-gray-600">{{ $store.getters.t('total_candidates') }}</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.totalCandidates }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
              <i class="fas fa-user-check text-green-600 text-xl"></i>
            </div>
            <div>
              <p class="text-sm text-gray-600">{{ $store.getters.t('active_candidates') }}</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.activeCandidates }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4">
              <i class="fas fa-clock text-yellow-600 text-xl"></i>
            </div>
            <div>
              <p class="text-sm text-gray-600">{{ $store.getters.t('pending_reviews') }}</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.pendingReviews }}</p>
            </div>
          </div>
        </div>
        
        <div class="bg-white rounded-lg shadow-sm border p-6">
          <div class="flex items-center">
            <div class="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
              <i class="fas fa-chart-line text-purple-600 text-xl"></i>
            </div>
            <div>
              <p class="text-sm text-gray-600">{{ $store.getters.t('recruitment_success_rate') }}</p>
              <p class="text-2xl font-bold text-gray-900">{{ stats.successRate }}%</p>
            </div>
          </div>
        </div>
      </div>

      <!-- 筛选和搜索 -->
      <div class="bg-white rounded-lg shadow-sm border p-8 mb-6">
        <div class="flex flex-wrap items-center gap-6">
          <div class="flex items-center space-x-3">
            <label class="text-base font-medium text-gray-700">{{ $store.getters.t('status_filter') }}:</label>
            <select 
              v-model="filters.status" 
              @change="loadResumes"
              class="border border-gray-300 rounded-md px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">{{ $store.getters.t('all_status') }}</option>
              <option value="待筛选">{{ $store.getters.t('pending_screening') }}</option>
              <option value="面试">{{ $store.getters.t('interview') }}</option>
              <option value="拒绝">{{ $store.getters.t('rejected_status') }}</option>
              <option value="录用">{{ $store.getters.t('hired_status') }}</option>
            </select>
          </div>
          
          <div class="flex items-center space-x-3">
            <label class="text-base font-medium text-gray-700">{{ $store.getters.t('search') }}:</label>
            <input 
              v-model="filters.keyword" 
              @keyup.enter="loadResumes"
              type="text" 
              :placeholder="$store.getters.t('resume_search_placeholder')"
              class="border border-gray-300 rounded-md px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          
          <button 
            @click="loadResumes"
            class="bg-purple-600 text-white px-6 py-3 rounded-md hover:bg-purple-700 transition-colors text-base font-medium"
          >
            <i class="fas fa-search mr-2"></i>
            {{ $store.getters.t('search') }}
          </button>
        </div>
      </div>

      <!-- 简历列表 -->
      <div class="bg-white rounded-lg shadow-sm border">
        <div class="p-8 border-b border-gray-200">
          <h3 class="text-xl font-semibold text-gray-900">{{ $store.getters.t('resume_list') }}</h3>
        </div>
        
        <!-- 加载状态 -->
        <div v-if="loading" class="p-8 text-center">
          <div class="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p class="text-sm text-gray-600">{{ $store.getters.t('loading_data') }}</p>
        </div>
        
        <!-- 错误状态 -->
        <div v-else-if="error" class="p-8 text-center">
          <i class="fas fa-exclamation-triangle text-red-500 text-2xl mb-2"></i>
          <p class="text-sm text-gray-600">{{ error }}</p>
          <button @click="loadResumes" class="mt-2 text-purple-600 hover:text-purple-700">
            {{ $store.getters.t('retry') }}
          </button>
        </div>
        
        <!-- 简历列表 -->
        <div v-else class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-gradient-to-r from-purple-50 to-blue-50 border-b-2 border-purple-200">
              <tr>
                <th class="px-8 py-3 text-left text-sm font-bold text-purple-700 uppercase tracking-wider">
                  {{ $store.getters.t('resume_id') }}
                </th>
                <th class="px-8 py-3 text-left text-sm font-bold text-purple-700 uppercase tracking-wider">
                  {{ $store.getters.t('candidate_name') }}
                </th>
                <th class="px-8 py-3 text-left text-sm font-bold text-purple-700 uppercase tracking-wider">
                   {{ $store.getters.t('position') }}
                 </th>
                 <th class="px-8 py-3 text-left text-sm font-bold text-purple-700 uppercase tracking-wider">
                   {{ $store.getters.t('status') }}
                 </th>
                 <th class="px-8 py-3 text-left text-sm font-bold text-purple-700 uppercase tracking-wider">
                   {{ $store.getters.t('resume_file') }}
                 </th>
                 <th class="px-8 py-3 text-left text-sm font-bold text-purple-700 uppercase tracking-wider">
                   {{ $store.getters.t('actions') }}
                 </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="resume in resumes" :key="resume.id" class="hover:bg-gray-50 transition-colors duration-200">
                <td class="px-8 py-2 whitespace-nowrap text-base text-gray-900 font-medium">
                  {{ resume.id }}
                </td>
                <td class="px-8 py-2 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                      <i class="fas fa-user text-gray-400"></i>
                    </div>
                    <div>
                      <div class="text-base font-medium text-gray-900">{{ resume.name }}</div>
                      <div class="text-sm text-gray-500">{{ resume.phone }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-8 py-2 whitespace-nowrap text-base text-gray-900">
                  {{ resume.jobName }}
                </td>
                <td class="px-8 py-2 whitespace-nowrap">
                  <span :class="getStatusClass(resume.status)" class="px-3 py-1 text-sm font-medium rounded-full">
                    {{ resume.status }}
                  </span>
                </td>
                 <td class="px-8 py-2 whitespace-nowrap text-base font-medium">
                   <button 
                     v-if="resume.filePath"
                     @click="downloadResume(resume)"
                     class="text-green-600 hover:text-green-900 px-2 py-1 rounded-md hover:bg-green-50 transition-colors text-sm"
                   >
                     <i class="fas fa-download mr-1"></i>
                     {{ $store.getters.t('download') }}
                   </button>
                   <span v-else class="text-gray-400 text-sm">{{ $store.getters.t('no_file') }}</span>
                 </td>
                 <td class="px-8 py-2 whitespace-nowrap text-base font-medium">
                   <div class="flex items-center space-x-2">
                     
                     <button 
                       @click="showStatusModal(resume)"
                       class="text-blue-600 hover:text-blue-900 px-2 py-1 rounded-md hover:bg-blue-50 transition-colors text-sm"
                     >
                       <i class="fas fa-edit mr-1"></i>
                       {{ $store.getters.t('change_status') }}
                     </button>
                   </div>
                 </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- 分页 -->
        <div v-if="!loading && !error && resumes.length > 0" class="px-8 py-4 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-base text-gray-600">
              {{ $store.getters.t('showing_records') }} {{ (currentPage - 1) * pageSize + 1 }} {{ $store.getters.t('to') }} {{ Math.min(currentPage * pageSize, totalRecords) }} {{ $store.getters.t('of') }} {{ totalRecords }} {{ $store.getters.t('records') }}
            </div>
            <div class="flex items-center space-x-3">
              <button 
                @click="changePage(currentPage - 1)"
                :disabled="currentPage === 1"
                class="px-4 py-2 text-base border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {{ $store.getters.t('previous_page') }}
              </button>
              <span class="px-4 py-2 text-base font-medium">{{ currentPage }} / {{ totalPages }}</span>
              <button 
                @click="changePage(currentPage + 1)"
                :disabled="currentPage === totalPages"
                class="px-4 py-2 text-base border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {{ $store.getters.t('next_page') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 状态更新模态框 -->
    <div v-if="showModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div class="mt-3">
          <h3 class="text-lg font-medium text-gray-900 mb-4">{{ $store.getters.t('update_resume_status') }}</h3>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $store.getters.t('resume_id') }}</label>
            <input 
              v-model="selectedResume.id" 
              type="text" 
              disabled
              class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
            />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">{{ $store.getters.t('resume_new_status') }}</label>
            <select 
              v-model="newStatus"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="待筛选">{{ $store.getters.t('pending_screening') }}</option>
              <option value="面试">{{ $store.getters.t('interview') }}</option>
              <option value="拒绝">{{ $store.getters.t('rejected_status') }}</option>
              <option value="录用">{{ $store.getters.t('hired_status') }}</option>
            </select>
          </div>
          <div class="flex justify-end space-x-3">
            <button 
              @click="closeModal"
              class="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {{ $store.getters.t('cancel') }}
            </button>
            <button 
              @click="updateStatus"
              :disabled="updating"
              class="px-4 py-2 text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
            >
              <span v-if="updating">{{ $store.getters.t('updating') }}</span>
              <span v-else>{{ $store.getters.t('confirm_update') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import resumeService from '@/services/resumeService'

export default {
  name: 'RecruitmentAgent',
  data() {
    return {
      loading: false,
      error: null,
      resumes: [],
      currentPage: 1,
      pageSize: 10,
      totalRecords: 0,
      totalPages: 0,
      filters: {
        status: '',
        keyword: ''
      },
      showModal: false,
      selectedResume: {},
      newStatus: '',
      updating: false,
      stats: {
        totalCandidates: 0,
        activeCandidates: 0,
        pendingReviews: 0,
        successRate: 0
      }
    }
  },
  mounted() {
    this.loadResumes()
  },
  methods: {
    /**
     * 加载简历列表
     */
    async loadResumes() {
      this.loading = true
      this.error = null
      
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          status: this.filters.status || null,
          keyword: this.filters.keyword || null
        }
        
        const result = await resumeService.getResumeList(params)
        
        console.log('API返回结果:', result) // 调试日志
        
        if (result && result.data) {
          this.resumes = result.data.list || result.data || []
          this.totalRecords = result.data.total || result.total || this.resumes.length
          this.totalPages = Math.ceil(this.totalRecords / this.pageSize)
          
          console.log('处理后的数据:', {
            resumes: this.resumes,
            totalRecords: this.totalRecords,
            totalPages: this.totalPages
          })
          
          // 更新统计数据
          this.updateStats()
        } else {
          this.resumes = []
          this.totalRecords = 0
          this.totalPages = 0
        }
      } catch (error) {
        console.error('加载简历列表失败:', error)
        this.error = error.message || '加载数据失败'
        this.resumes = []
      } finally {
        this.loading = false
      }
    },
    
    /**
     * 更新统计数据
     */
    updateStats() {
      // 这里可以根据实际数据计算统计信息
      this.stats.totalCandidates = this.totalRecords
      this.stats.activeCandidates = this.resumes.filter(r => r.status === '录用').length
      this.stats.pendingReviews = this.resumes.filter(r => r.status === '待筛选').length
      this.stats.successRate = this.totalRecords > 0 ? Math.round((this.stats.activeCandidates / this.totalRecords) * 100) : 0
    },
    
    /**
     * 切换页面
     */
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page
        this.loadResumes()
      }
    },
    
    /**
     * 获取状态样式类
     */
    getStatusClass(status) {
      const statusClasses = {
        '待筛选': 'bg-yellow-100 text-yellow-800',
        '面试': 'bg-blue-100 text-blue-800',
        '拒绝': 'bg-red-100 text-red-800',
        '录用': 'bg-green-100 text-green-800'
      }
      return statusClasses[status] || 'bg-gray-100 text-gray-800'
    },
    
    /**
     * 查看简历详情
     */
    viewResume(resume) {
      // 这里可以实现查看简历详情的功能
      console.log('查看简历:', resume)
      alert(`查看简历: ${resume.name}`)
    },
    
    /**
     * 显示状态更新模态框
     */
    showStatusModal(resume) {
      this.selectedResume = { ...resume }
      this.newStatus = resume.status
      this.showModal = true
    },
    
    /**
     * 关闭模态框
     */
    closeModal() {
      this.showModal = false
      this.selectedResume = {}
      this.newStatus = ''
    },
    
    /**
     * 更新简历状态
     */
    async updateStatus() {
      if (!this.newStatus) {
        alert('请选择新状态')
        return
      }
      
      this.updating = true
      
      try {
        await resumeService.updateResumeStatus(this.selectedResume.id, this.newStatus)
        
        // 更新本地数据
        const index = this.resumes.findIndex(r => r.id === this.selectedResume.id)
        if (index !== -1) {
          this.resumes[index].status = this.newStatus
        }
        
        // 更新统计数据
        this.updateStats()
        
        this.closeModal()
        alert('状态更新成功')
      } catch (error) {
        console.error('更新状态失败:', error)
        alert('状态更新失败: ' + (error.message || '未知错误'))
      } finally {
        this.updating = false
      }
    },
    
    /**
     * 格式化日期
     */
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN')
    },
    
    /**
     * 下载简历文件
     */
    downloadResume(resume) {
      if (!resume.filePath) {
        alert('没有可下载的文件')
        return
      }
      
      try {
        // 创建一个临时的a标签来下载文件
        const link = document.createElement('a')
        link.href = resume.filePath
        link.download = `${resume.name}_简历.${this.getFileExtension(resume.filePath)}`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('下载文件失败:', error)
        alert('下载文件失败')
      }
    },
    
    /**
     * 获取文件扩展名
     */
    getFileExtension(filePath) {
      if (!filePath) return 'pdf'
      const parts = filePath.split('.')
      return parts.length > 1 ? parts[parts.length - 1] : 'pdf'
    }
  }
}
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style> 